import { ChangeDetectionStrategy, Component, input, OnInit } from '@angular/core';

import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { toLocaleISOString } from '@softline/core';
import { FieldValidationDirective, IconComponent, InputComponent, MultilineInputComponent, ResolvableModal, Validators } from '@softline/ui-core';
import { DateInputComponent } from '@softline/ui-core/date-input';
import { TimeInputComponent } from '@softline/ui-core/date-input';
import { setTimeOfDate } from '../../functions/set-time-of-date.function';
import { AdditionalData } from '../../types/additional-data';
import { RosterContent } from '../../types/roster-content';

@Component({
  selector: 'soft-roster-content-edit',
  imports: [
    ReactiveFormsModule,
    FieldValidationDirective,
    DateInputComponent,
    TimeInputComponent,
    InputComponent,
    MultilineInputComponent,
    IconComponent,
  ],
  templateUrl: './roster-content-edit.component.html',
  styleUrl: './roster-content-edit.component.css',
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RosterContentEditComponent extends ResolvableModal<null> implements OnInit {
  readonly event = input.required<RosterContent>();
  readonly additionalData = input.required<AdditionalData>();
  readonly onSave = input.required<(_: RosterContent) => Promise<void>>();

  readonly form = new FormGroup({
    taskLabel: new FormControl<string | null>(null, [Validators.required()]),
    begintime: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required(), Validators.min(3)],
    }),
    beginday: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    endday: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    endtime: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required()],
    }),
    comment: new FormControl<string | null>(null),
    state: new FormControl<string | null>(null),
    type: new FormControl<string | null>(null),
  });

  ngOnInit() {
    const data = this.additionalData();
    this.form.patchValue({
      taskLabel: data.task.name,
      begintime: `T${(data.begintime ?? '').split('T')[1]}`,
      beginday: data.begintime,
      endday: data.endtime,
      endtime: `T${(data.endtime ?? '').split('T')[1]}`,
      comment: data.comment,
      state: data.state,
      type: data.type,
    } as any);
  }

  async onSubmit(): Promise<void> {
    if (!this.form.valid) return;
    const formValue = this.form.getRawValue();
    const startDate = setTimeOfDate(formValue.beginday, formValue.begintime);
    const endDate = setTimeOfDate(formValue.endday, formValue.endtime);

    if (!startDate || !endDate) return;

    const start = toLocaleISOString(startDate);
    const end = toLocaleISOString(endDate);

    // Backend-only response fields must not be sent back
    const additionalData = { ...this.additionalData() };
    delete additionalData.billable;
    delete additionalData.creatorUser;
    delete additionalData.createdAt;
    const body = {
      ...this.event(),
      date: start,
      start,
      end,
      additionalData: {
        ...additionalData,
        begintime: start,
        endtime: end,
        comment: formValue.comment,
        state: formValue.state,
        type: formValue.type,
        task: {
          ...additionalData.task,
          label: formValue.taskLabel,
        },
        additionaldetail: [],
      },
    } as any as RosterContent;

    await this.onSave()(body);
    this.resolve(null);
  }
}
